import { Links, OptionalLinks } from 'src/types/api-utilities';
import { MonetaryValue } from 'src/types/capacity-management';

export type AggregateEuRootResponse = {
  _links?: Links<'self' | 'getFinancialSecurityOptions'> &
    OptionalLinks<
      | 'requestOrganisationSubscription'
      | 'manageOrganisationSubscription'
      // only there if requestOrganisationSubscription is there as well
      | 'uploadDeclarationOfHonour'
      | 'getFinancialSecurityOptions'
      | 'getLocationOptions'
      | 'settings'
      | 'demands'
      | 'aggregatedDemands'
      | 'infoSheets'
      | 'tenders'
      | 'buyerMatches'
      | 'sellerMatches'
      | 'consortiums'
      | 'matches'
      | 'sellerMatches'
      | 'consortia'
    >;
  organisationSubscriptionDetails?: {
    state: OrganisationSubscriptionStatus;
    subscriptionId: string;
    organisationTypes: OrganisationType[];
  };
  lngProcurementProcess?: ProcurementProcess;
  nbpProcurementProcess?: ProcurementProcess;
};

export type Quantity = {
  startOfMonth: string;
  amount: number | null;
};

export type Location = {
  id: string;
  displayName: string;
  tooltip?: string;
  terminals?: LngTerminal[];
};

export type LngTerminal = {
  id: string;
  country: string;
  displayName: string;
};

export type ProcurementProcess = {
  _links?: // <'self'> &
  // these links are conditional available based on the current phase of the process
  OptionalLinks<
    | 'myDemands'
    | 'myInfoSheets'
    | 'myTenders'
    | 'myBuyerMatches'
    | 'mySellerMatches'
    // check what happens with these:
    | 'submitDemand'
    | 'getAggregatedDemands'
    | 'mySellerMatches'
  >;
  displayProcurementId: string;
  processType: ProcessType;
  procurementProcessId: string;
  demandCollectionWindow: { start: string; end: string };
  informationSheetFinalizationWindow?: { start: string; end: string };
  tenderingWindow?: { start: string; end: string };
  matchingPublication?: string;
  earliestDemandStart: string;
  demandMonths: DemandMonth[];
  latestDemandStart: string;
  minimalMonthlyAmount: number;
};

export type DemandMonth = {
  startOfMonth: string;
  year: number;
  month: number;
};

export enum ProcessType {
  LNG = 'LNG_PROCUREMENT',
  NBP = 'NBP_PROCUREMENT',
}

export type GasDemandsParams = {
  limit?: number;
  offset?: number;
  processType: ProcessType[];
  currentProcessOnly: boolean;
};

export type MyDemandsResponse = {
  _links?: Links<'self' | 'instance'> &
    OptionalLinks<'submitLngDemand' | 'submitNbpDemand'>;
  total: number;
  offset: number;
  limit: number;
  _embedded: {
    items: MyDemand[];
  };
};

export type MyDemand = {
  _links?: Links<'self'> & OptionalLinks<'delete' | 'update'>;
  procurementProcessId: string;
  demandAtLocationId: string;
  processType: ProcessType;
  consortiumId?: string;
  consortiumName?: string;
  location: {
    id: string;
    displayName: string;
  };
  quantities: Quantity[];
};

export type LNGMyDemandsResponse = {
  _links?: Links<'self'>;
  _embedded: { items: MyDemand[] };
};

export type NBPMyDemandsResponse = {
  _links?: Links<'self'>;
  _embedded: { items: MyDemand[] };
};

export type WriteLNGDemand = {
  locationId: string;
  quantities: Quantity[];
};

export type LNGProcurementProcessAggregatedDemandsResponse = {
  _links?: Links<'self'>;
  _embedded?: {
    items: MyDemand[];
  };
};

export type NBPProcurementProcessAggregatedDemandsResponse = {
  _links?: Links<'self'>;
  _embedded?: {
    items: MyDemand[];
  };
};

export type WriteNBPDemand = {
  locationId: string;
  quantities: Quantity[];
};

export type ProcurementProcessAggregatedDemandsResponse = {
  _links?: Links<'self'>;
  _embedded?: {
    items: MyDemand[];
  };
};

export type WriteGasDemand = {
  locationId: string;
  consortiumId?: string | null;
  quantities: Quantity[];
};

export type UpdateGasDemand = {
  quantities: Quantity[];
};

// quantity unit is always "GWH"
export type Amount = { value: number; quantityUnit: string };

export enum OrganisationSubscriptionStatus {
  REQUESTED = 'REQUESTED',
  APPROVED = 'APPROVED',
  REVOKED = 'REVOKED',
  REJECTED = 'REJECTED',
  CANCELLED = 'CANCELLED',
}

export type OrganisationType = 'BUYER' | 'SELLER';

type FinancialSecurity = {
  id: string;
  displayName: string;
};

export type OrganisationSubscriptionResponse = {
  _links?: Links<'self'>;
  organisationId: string;
  subscriptionId: string;
  status: OrganisationSubscriptionStatus;
  organisationTypes: OrganisationType[];
  financialSecurities: FinancialSecurity[];
};

export type WriteOrganisationSubscription = {
  organisationTypes: string[];
  preferredLocations: string[];
  // undefined for sellers
  financialSecurities?: string[];
  // just the file uuid
  declarationOfHonourFileId: string;
};

export type LocationsResponse = {
  lngLocations: Location[];
  nbpLocations: Location[];
};

export type InformationSheetsParams = {
  limit?: number;
  offset?: number;
  processType?: ProcessType[];
};

export type InformationSheetsResponse = {
  _links?: Links<'self' | 'first'> & OptionalLinks<'next' | 'prev'>;
  _embedded: {
    items: InformationSheetDetails[];
  };
  total: number;
  limit: number;
  offset: number;
};

export type InformationSheetDetails = {
  _links?: OptionalLinks<'decline'>;
  id: string;
  state: 'DEFAULT' | 'DECLINED';
  processType: ProcessType;
  location: Location;
  startOfMonth: string;
  maxTotalAmount: number;
  buyerAmount: number;
  consortium?: ConsortiumDisplay;
};

export type FinancialSecurityOptionsResponse = {
  _links?: Links<'self'>;
  _embedded: {
    items: FinancialSecurity[];
  };
};

export type TenderParams = {
  limit?: number;
  offset?: number;
  processType?: ProcessType[];
};

export type TendersResponse = {
  _links?: Links<'self' | 'first' | 'instance'> &
    OptionalLinks<'next' | 'prev'>;
  _embedded: {
    items: TenderDetails[];
  };
  total: number;
  limit: number;
  offset: number;
};

export type TenderDetails = {
  _links?: Links<'self'> &
    OptionalLinks<'submitOffers' | 'editOffers' | 'deleteOffers'>;
  id: string;
  displayTenderId: string;
  processType: ProcessType;
  location: Location;
  startOfMonth: string;
  totalAmount: number;
  offers: TenderOffer[];
};

export type TenderOffer = {
  id: string;
  quantity: number;
  price: MonetaryValue;
  validThrough: string;
};

export type WriteOffers = {
  offers: WriteOffer[];
};

export type WriteOffer = {
  quantity: number | null;
  price: {
    value: number | null;
    currency: string;
  };
  validThrough?: string | null;
};

export type GetBuyerMatchesParams = {
  processType?: ProcessType[];
  locationId?: string[];
  startOfMonthStart?: string | null;
  startOfMonthEnd?: string | null;
};

export type ConsortiumDisplay = {
  id: string;
  displayName: string;
};

export type BuyerMatch = {
  buyerMatchId: string;
  demandId: string;
  tenderId: string;
  procurementProcessId: string;
  processType: ProcessType;
  demandDisplayId: string;
  tenderDisplayId: string;
  processDisplayId: string;
  consortium?: ConsortiumDisplay;
  location: Location;
  startOfMonth: string;
  demandedCapacity: number;
  matchedCapacity: number;
  averagePrice: number;
};

export type BuyerMatches = {
  _embedded: { items: BuyerMatch[] };
  total: number;
  limit: number;
  offset: number;
};

export type SellerMatchesResponse = {
  _embedded: { items: SellerMatch[] };
  total: number;
  limit: number;
  offset: number;
};

export type SellerMatch = {
  _links?: Links<'self'>;
  procurementProcessId: string;
  processType: ProcessType;
  processDisplayId: string;
  location: Location;
  startOfMonth: string;
  totalOfferedCapacity: number;
  totalMatchedCapacity: number;
  sellerMatchId: string;
};

export type GetMySellerMatchesParams = {
  processType?: ProcessType[];
  locationId?: string[];
  startOfMonthStart?: string | null;
  startOfMonthEnd?: string | null;
};

export type ConsortiaResponse = {
  _links?: Links<'self'> & OptionalLinks<'first' | 'instance' | 'create'>;
  total: number;
  limit: number;
  offset: number;
  _embedded: {
    items: Consortium[];
  };
};

export type Consortium = {
  _links: Links<'self'> & OptionalLinks<'update'>;
  id: string;
  name: string;
  members: {
    organisationName: string;
    organisationEic?: string;
    organisationVat: string;
  }[];
};
